/**
 * ValidationDetail (C-7) — single validation event: metadata, lifecycle FSM
 * actions (approval requires an outcome), Related Records (C-4), history.
 * Route: /validation/$id
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import styles from '../Recalls/Recalls.module.css';
import { RelatedRecords } from '../../components/RelatedRecords';
import {
  useValidationDetailQuery, useValidationTransition,
  VALIDATION_FSM, VAL_STATUS_LABEL, VAL_TYPE_LABEL, VAL_OUTCOME_LABEL,
} from './useValidation';

function fmtDT(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(iso); }
}
function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function outcomeClass(o: string | null) {
  if (o === 'PASS') return styles.ocGood;
  if (o === 'PASS_WITH_DEVIATIONS') return styles.ocWarn;
  if (o === 'FAIL') return styles.ocBad;
  return '';
}

export function ValidationDetail({ validationId }: { validationId: string }) {
  const q = useValidationDetailQuery(validationId);
  const transition = useValidationTransition(validationId);
  const [msg, setMsg] = useState<string | null>(null);
  const [outcome, setOutcome] = useState('PASS');

  if (q.isLoading) return <main className={styles.page}><div className={styles.empty}>Loading validation event…</div></main>;
  if (q.isError || !q.data) return <main className={styles.page}><div className={styles.empty}>Validation event not found. <Link to="/validation">Back to validation</Link></div></main>;
  const v = q.data;
  const next = VALIDATION_FSM[v.status] ?? [];

  async function doTransition(to: string) {
    setMsg(null);
    try {
      // Approval requires an outcome (PASS / PASS_WITH_DEVIATIONS / FAIL).
      await transition.mutateAsync(to === 'APPROVED' ? { toStatus: to, outcome } : { toStatus: to });
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Transition failed.'); }
  }

  return (
    <main className={styles.page}>
      <nav className={styles.crumbs} aria-label="Breadcrumb">
        <Link to="/validation" className={styles.crumb}>Validation</Link>
        <span className={styles.crumbSep} aria-hidden>/</span>
        <span>{v.validationNumber}</span>
      </nav>

      <header className={styles.detailHead}>
        <div>
          <h1 className={styles.detailTitle}><span className={styles.mono}>{v.validationNumber}</span> · {v.title}</h1>
          <div className={styles.subline}>
            <span className={styles.statusPill}>{VAL_STATUS_LABEL[v.status] ?? v.status}</span>
            <span className={styles.pill}>{VAL_TYPE_LABEL[v.validationType] ?? v.validationType}</span>
            {v.outcome && <span className={`${styles.pill} ${outcomeClass(v.outcome)}`}>{VAL_OUTCOME_LABEL[v.outcome] ?? v.outcome}</span>}
          </div>
        </div>
        <div className={styles.actions}>
          {next.includes('APPROVED') && (
            <select aria-label="Approval outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)} className={styles.select}>
              {Object.keys(VAL_OUTCOME_LABEL).map((o) => <option key={o} value={o}>{VAL_OUTCOME_LABEL[o]}</option>)}
            </select>
          )}
          {next.length === 0 ? <span className={styles.terminal}>Terminal state</span> : next.map((to) => (
            <button key={to} type="button" className={`${styles.btn} ${to === 'CANCELLED' ? styles.btnNeutral : styles.btnPrimary}`}
              onClick={() => void doTransition(to)} disabled={transition.isPending}>→ {VAL_STATUS_LABEL[to] ?? to}</button>
          ))}
        </div>
      </header>
      {msg && <div className={styles.err} role="alert">{msg}</div>}

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Validation detail</h2>
        <dl className={styles.metaGrid}>
          <div><dt>Type</dt><dd>{VAL_TYPE_LABEL[v.validationType] ?? v.validationType}</dd></div>
          <div><dt>System / equipment</dt><dd>{v.systemRef ?? '—'}</dd></div>
          <div><dt>Protocol number</dt><dd>{v.protocolNumber ?? '—'}</dd></div>
          <div><dt>Approach</dt><dd>{v.approach ?? '—'}</dd></div>
          <div><dt>Outcome</dt><dd>{v.outcome ? (VAL_OUTCOME_LABEL[v.outcome] ?? v.outcome) : '—'}</dd></div>
          <div><dt>Owner</dt><dd>{v.ownerName ?? '—'}</dd></div>
          <div><dt>Protocol approved</dt><dd>{fmtDate(v.protocolApprovedDate)}</dd></div>
          <div><dt>Execution start</dt><dd>{fmtDate(v.executionStartDate)}</dd></div>
          <div><dt>Execution end</dt><dd>{fmtDate(v.executionEndDate)}</dd></div>
          <div><dt>Report approved</dt><dd>{fmtDate(v.reportApprovedDate)}</dd></div>
          <div><dt>Target completion</dt><dd>{fmtDate(v.targetDate)}</dd></div>
          <div><dt>Opened</dt><dd>{fmtDT(v.openedAt)}</dd></div>
        </dl>
        {v.notes && <div className={styles.descBlock}><strong>Notes</strong><p>{v.notes}</p></div>}
      </section>

      {/* C-4 — Related Records (Deviation / CAPA / OOS / Risk / CC / Complaint). */}
      <RelatedRecords entityType="VALIDATION" entityId={v.validationId} />

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>History</h2>
        <ol className={styles.timeline}>
          {(v.history ?? []).map((h, i) => (
            <li key={i} className={styles.tlRow}>
              <span className={styles.tlWhen}>{fmtDT(h.at_utc)}</span>
              <span className={styles.tlWhat}>
                {h.event_kind === 'VALIDATION_TRANSITION' && h.from_status
                  ? `${VAL_STATUS_LABEL[h.from_status] ?? h.from_status} → ${VAL_STATUS_LABEL[h.to_status ?? ''] ?? h.to_status}`
                  : h.event_kind.replaceAll('_', ' ').toLowerCase()}
                {h.note ? <span className={styles.tlNote}> — {h.note}</span> : null}
              </span>
              {h.actor_name && <span className={styles.sub}>{h.actor_name}</span>}
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}
